import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";
import { mockBranches } from "@/mocks/restaurantStock";

interface Branch {
  id: string;
  dealer_id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  contact_person: string;
  is_active: boolean;
  created_at: string;
}

export default function RestaurantBranchesPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId;
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [saving, setSaving] = useState(false);

  const [formName, setFormName] = useState("");
  const [formAddress, setFormAddress] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formContact, setFormContact] = useState("");
  const [formActive, setFormActive] = useState(true);

  const fetchBranches = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("branches")
        .select("*")
        .eq("dealer_id", dealerId)
        .order("name");

      if (err) throw err;
      if (!data || data.length === 0) {
        setBranches(mockBranches as Branch[]);
      } else {
        setBranches(data);
      }
    } catch (e: any) {
      setError(e.message || "Şubeler yüklenirken hata oluştu.");
      setBranches(mockBranches as Branch[]);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const activeBranches = useMemo(() => branches.filter((b) => b.is_active), [branches]);
  const inactiveBranches = useMemo(() => branches.filter((b) => !b.is_active), [branches]);

  const openAdd = () => {
    setEditingBranch(null);
    setFormName("");
    setFormAddress("");
    setFormPhone("");
    setFormEmail("");
    setFormContact("");
    setFormActive(true);
    setShowModal(true);
  };

  const openEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setFormName(branch.name);
    setFormAddress(branch.address || "");
    setFormPhone(branch.phone || "");
    setFormEmail(branch.email || "");
    setFormContact(branch.contact_person || "");
    setFormActive(branch.is_active);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBranch(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim() || !dealerId) return;

    setSaving(true);
    try {
      const supabase = getSupabase();
      if (editingBranch) {
        const { error: updateErr } = await supabase
          .from("branches")
          .update({
            name: formName.trim(),
            address: formAddress.trim(),
            phone: formPhone.trim(),
            email: formEmail.trim(),
            contact_person: formContact.trim(),
            is_active: formActive,
            updated_at: new Date().toISOString(),
          })
          .eq("id", editingBranch.id);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase
          .from("branches")
          .insert({
            dealer_id: dealerId,
            name: formName.trim(),
            address: formAddress.trim(),
            phone: formPhone.trim(),
            email: formEmail.trim(),
            contact_person: formContact.trim(),
            is_active: formActive,
          });
        if (insertErr) throw insertErr;
      }

      closeModal();
      fetchBranches();
    } catch (e: any) {
      alert(e.message || "Şube kaydedilirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (branch: Branch) => {
    try {
      const supabase = getSupabase();
      const { error: updateErr } = await supabase
        .from("branches")
        .update({ is_active: !branch.is_active, updated_at: new Date().toISOString() })
        .eq("id", branch.id);
      if (updateErr) throw updateErr;

      setBranches((prev) =>
        prev.map((b) => (b.id === branch.id ? { ...b, is_active: !b.is_active } : b))
      );
    } catch (e: any) {
      alert(e.message || "Durum güncellenirken hata oluştu.");
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  if (!user?.hasRestaurantBranches) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-20 h-20 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-line text-3xl text-accent-500"></i>
        </div>
        <h4 className="text-lg font-semibold text-foreground-950 mb-1">Erişim Kısıtlandı</h4>
        <p className="text-sm text-foreground-500 mb-4 text-center max-w-sm">
          Bu sayfaya erişmek için bayi yöneticinizden restoran modülü yetkisi talep edin.
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Dashboard'a Dön
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-store-2-line text-secondary-600"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950">Şube Yönetimi</h1>
          </div>
          <p className="text-sm text-foreground-500 mt-0.5">
            Restoran şubelerini ekleyin, düzenleyin ve yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Yeni Şube
          </button>
          <button
            onClick={fetchBranches}
            className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Yenile
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Şube</p>
          <p className="text-lg font-bold text-foreground-950">{branches.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-xs text-primary-600"></i>
            </div>
            <p className="text-xs text-foreground-400">Aktif</p>
          </div>
          <p className="text-lg font-bold text-primary-600">{activeBranches.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-1">
            <div className="w-5 h-5 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-pause-circle-line text-xs text-foreground-500"></i>
            </div>
            <p className="text-xs text-foreground-400">Pasif</p>
          </div>
          <p className="text-lg font-bold text-foreground-500">{inactiveBranches.length}</p>
        </div>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={fetchBranches} className="ml-auto text-accent-700 font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : branches.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-xl py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-store-2-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 font-medium">Henüz şube eklenmemiş</p>
          <p className="text-xs text-foreground-400 mt-1 mb-4">İlk restoran şubenizi ekleyerek başlayın</p>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Yeni Şube Ekle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <div
              key={branch.id}
              className={`bg-background-50 border border-background-200/70 rounded-xl p-5 transition-all ${
                !branch.is_active ? "opacity-60" : ""
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    branch.is_active ? "bg-primary-100" : "bg-foreground-100"
                  }`}>
                    <i className={`ri-store-2-line text-lg ${
                      branch.is_active ? "text-primary-600" : "text-foreground-400"
                    }`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-950 text-sm">{branch.name}</h3>
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium mt-0.5 ${
                      branch.is_active
                        ? "bg-primary-100 text-primary-700"
                        : "bg-foreground-100 text-foreground-500"
                    }`}>
                      {branch.is_active ? "Aktif" : "Pasif"}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEdit(branch)}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                    title="Düzenle"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => toggleActive(branch)}
                    className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors cursor-pointer ${
                      branch.is_active
                        ? "text-foreground-400 hover:text-accent-500 hover:bg-accent-50"
                        : "text-foreground-400 hover:text-primary-600 hover:bg-primary-50"
                    }`}
                    title={branch.is_active ? "Pasif yap" : "Aktif yap"}
                  >
                    <i className={branch.is_active ? "ri-pause-circle-line text-sm" : "ri-play-circle-line text-sm"}></i>
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                {branch.address && (
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-line text-foreground-400 mt-0.5 shrink-0"></i>
                    <span className="text-foreground-600 text-xs">{branch.address}</span>
                  </div>
                )}
                {branch.phone && (
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-foreground-400 shrink-0"></i>
                    <span className="text-foreground-600 text-xs">{branch.phone}</span>
                  </div>
                )}
                {branch.email && (
                  <div className="flex items-center gap-2">
                    <i className="ri-mail-line text-foreground-400 shrink-0"></i>
                    <span className="text-foreground-600 text-xs">{branch.email}</span>
                  </div>
                )}
                {branch.contact_person && (
                  <div className="flex items-center gap-2">
                    <i className="ri-user-line text-foreground-400 shrink-0"></i>
                    <span className="text-foreground-600 text-xs">{branch.contact_person}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center justify-between text-xs">
                <span className="text-foreground-400">Eklenme: {formatDate(branch.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={closeModal}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center">
                      <i className={editingBranch ? "ri-edit-line text-secondary-600" : "ri-add-line text-secondary-600"}></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950">
                      {editingBranch ? "Şube Düzenle" : "Yeni Şube Ekle"}
                    </h3>
                  </div>
                  <button onClick={closeModal} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Şube Adı *</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="Örn: ART Restaurant - Merkez"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Adres</label>
                    <textarea
                      value={formAddress}
                      onChange={(e) => setFormAddress(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400 resize-none"
                      placeholder="Şube adresi"
                    ></textarea>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">Telefon</label>
                      <input
                        type="text"
                        value={formPhone}
                        onChange={(e) => setFormPhone(e.target.value)}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                        placeholder="0212 555 0101"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">E-posta</label>
                      <input
                        type="email"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                        placeholder="sube@restaurant.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1">Yetkili Kişi</label>
                    <input
                      type="text"
                      value={formContact}
                      onChange={(e) => setFormContact(e.target.value)}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-secondary-400"
                      placeholder="Şube yetkilisi adı"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="branchActive"
                      checked={formActive}
                      onChange={(e) => setFormActive(e.target.checked)}
                      className="w-4 h-4 rounded border-background-200/70 text-primary-500 focus:ring-primary-400 cursor-pointer"
                    />
                    <label htmlFor="branchActive" className="text-sm text-foreground-600 cursor-pointer">Aktif şube</label>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      İptal
                    </button>
                    <button
                      type="submit"
                      disabled={!formName.trim() || saving}
                      className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? (
                        <span className="flex items-center justify-center gap-1.5">
                          <i className="ri-loader-4-line animate-spin"></i>
                          Kaydediliyor
                        </span>
                      ) : editingBranch ? "Güncelle" : "Ekle"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}